import { memo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Select } from 'antd';
import useSmartField from 'hooks/useSmartField';
import dayjs, { Dayjs } from 'lib/dayjs';
import { css } from '@emotion/css';
import { DepartmentsSelect } from 'views/Dashboard/ImplementData/components/Filter/DepartmentSelect';
import {
  usePlatformSelect,
  useStoreInfoSelect,
} from 'views/Dashboard/ImplementData/components/Filter/Provider';
import { LeftOutlined } from '@ant-design/icons';
import { useCustomTypeContext } from './CutstomTypeContext';
import { DatePicker } from 'components/DatePicker';

const Header = memo((props: { onBack?: () => void }) => {
  const { onBack } = props;
  const scene = 'VIEW_PENDING_DATA';
  const { t } = useTranslate();
  const { field: timeRange } = useSmartField('timeRange');
  const {
    field: { value: departments, onChange: setDepartments },
  } = useSmartField('departmentIds');
  const {
    field: { value: platform, onChange: setPlatform },
  } = useSmartField('platform');
  const {
    field: { value: storeInfo, onChange: setStoreInfo },
  } = useSmartField('storeInfo');
  const {
    field: { value: types, onChange: setTypes },
  } = useSmartField('types');
  const dates = timeRange.value?.map((date) => dayjs(date, 'YYYY-MM-DD'));
  const [presetDate, setPresetDate] = useState(() => {
    if (timeRange.value?.[0] && timeRange.value?.[1]) {
      const diff = dayjs(timeRange.value?.[1], 'YYYY-MM-DD').diff(
        dayjs(timeRange.value?.[0], 'YYYY-MM-DD'),
        'days',
      );
      if (diff === 1) {
        return 1;
      }
      if (diff === 6) {
        return 2;
      }
      if (diff === 14) {
        return 3;
      }
    }
    return 2;
  });
  const { regionInfo, platformOptions, platformSelect } = usePlatformSelect({
    departments,
    scene,
    showPlatformAll: true,
    value: platform,
    onChange: (value) => {
      setPlatform(value);
      setStoreInfo({});
    },
  });
  const { storeInfoSelect } = useStoreInfoSelect({
    regionInfo,
    platform,
    departments,
    platformOptions,
    storeType: undefined,
    storeInfo,
    onChange: (storeInfo) => {
      setStoreInfo(storeInfo);
    },
    scene,
  });

  return (
    <div className="py-[16px] bg-[var(--fett-bg-app)] sticky top-0 z-[99]">
      <div className="bg-[var(--fett-bg-panel)]">
        <div className="px-[16px] py-[17px] leading-[22px] flex border-solid border-0 border-b border-[var(--fett-border-default)]">
          {onBack && (
            <div
              className="text-[var(--fett-text-secondary)] ml-[4px] mr-[16px] cursor-pointer"
              onClick={onBack}
            >
              <LeftOutlined style={{ color: 'var(--fett-text-secondary)' }} />
              {t('phrase.返回')}
            </div>
          )}
          <div className="text-[16px] leading-[22px] text-[var(--fett-text-primary)] font-medium">
            {t('ViewDashboard.nav.待办数据')}
          </div>
        </div>

        <div className="min-h-[56px] gap-4 px-[16px] py-[12px] flex justify-between flex-wrap">
          <div className="flex-grow flex items-center gap-[8px]">
            <DepartmentsSelect
              className="min-w-[170px]"
              dashboardType="todo"
              value={departments}
              onChange={(value) => {
                setDepartments(value);
                // 这里为啥要设置2次呢，因为storeSelect组件需要platform的销毁才能重置状态，hardcode就完事了。
                setPlatform(undefined);
                setTimeout(() => {
                  setPlatform('ALL');
                });
                // hardcode
                if (!(departments?.length > 0 && departments?.length === value.length)) {
                  setStoreInfo({});
                }
              }}
            />
            {platformSelect}
            {storeInfoSelect}
            <TypeSelect
              value={types}
              onChange={(value) => {
                setTypes(value);
              }}
            />
          </div>
          <div className="inline-flex items-center">
            <div
              className={`mr-[16px] text-nowrap cursor-pointer ${
                presetDate === 1 ? 'text-primary' : ''
              } `}
              onClick={() => {
                setPresetDate(1);
                timeRange.onChange([
                  dayjs().subtract(1, 'days').format('YYYY-MM-DD'),
                  dayjs().subtract(1, 'days').format('YYYY-MM-DD'),
                ]);
              }}
            >
              {t('remind.昨天')}
            </div>
            <div
              className={`mr-[16px] text-nowrap cursor-pointer ${
                presetDate === 2 ? 'text-primary' : ''
              } `}
              onClick={() => {
                setPresetDate(2);
                timeRange.onChange([
                  dayjs().subtract(6, 'days').format('YYYY-MM-DD'),
                  dayjs().format('YYYY-MM-DD'),
                ]);
              }}
            >
              {t('remind.近x天', { x: 7 })}
            </div>
            <div
              className={`mr-[16px] text-nowrap cursor-pointer ${
                presetDate === 3 ? 'text-primary' : ''
              } `}
              onClick={() => {
                setPresetDate(3);
                timeRange.onChange([
                  dayjs().subtract(14, 'days').format('YYYY-MM-DD'),
                  dayjs().format('YYYY-MM-DD'),
                ]);
              }}
            >
              {t('remind.近x天', { x: 15 })}
            </div>
            <DatePicker.RangePicker
              format="YYYY-MM-DD"
              showTime={false}
              value={dates}
              onChange={(dates) => {
                timeRange.onChange([
                  dates?.[0]?.format('YYYY-MM-DD'),
                  dates?.[1]?.format('YYYY-MM-DD'),
                ]);
              }}
              allowClear={false}
              disabledDate={(current: Dayjs) => {
                if (!timeRange) {
                  return false;
                }
                const tooLate = timeRange[0] && current.diff(timeRange[0], 'days') >= 30;
                const tooEarly = timeRange[1] && timeRange[1].diff(current, 'days') >= 30;
                return !!tooEarly || !!tooLate;
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

const TypeSelect = memo((props: { value: number[]; onChange: (val: number[]) => void }) => {
  const { value, onChange } = props;
  const { t } = useTranslate();
  const { types } = useCustomTypeContext();

  return (
    <Select
      mode="multiple"
      maxTagCount={1}
      placeholder={t('aide.选择类型')}
      className={css`
        width: 170px;
        .ant-select-selection-item {
          max-width: 70px;
        }
      `}
      value={value}
      allowClear
      onChange={onChange}
    >
      {types.map((item) => {
        return <Select.Option value={item.id}>{item.name}</Select.Option>;
      })}
    </Select>
  );
});

export default Header;
